/**
 * UploadModal — Phase 2.8 in-scene replacement for navigating to `/upload`.
 *
 * Renders the existing `<UploadFlowSteps>` UI inside a centred pixel-card on
 * top of a *translucent* `bg-pop-night/70` backdrop so the home scene stays
 * visible behind it — the avatar in the scene plays the
 * `character-taking-picture` pose while this is open, which only works if the
 * scene is actually rendered behind the modal.
 *
 * Close affordances:
 *   - ESC key            → calls `onClose`
 *   - backdrop click     → calls `onClose`
 *   - "Zrušit" in steps  → calls `onClose` (wired via UploadFlowSteps' onCancel)
 *
 * On confirm the parent decides what happens next (close the modal +
 * transition the scene state to `recs-revealed`). The modal does NOT itself
 * trigger any contract-confirm mutation — that's the job of the scene's
 * confirm handler, which uses the OcrResult returned by the upload flow.
 *
 * `data-testid="upload-modal"` + `data-zone={zone}` are exposed for Playwright.
 */
import { useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'

import {
  UploadFlowSteps,
  type UploadZone,
} from '@/components/UploadFlowSteps'
import type { ContractUploadResponse } from '@shared/schemas'

export type UploadModalProps = {
  zone: UploadZone
  open: boolean
  onClose: () => void
  /**
   * Receives the full upload envelope from the BE. The parent (HomeScene)
   * uses `result.contract.id` to fire the confirm mutation.
   */
  onConfirm: (result: ContractUploadResponse) => void
}

export function UploadModal({
  zone,
  open,
  onClose,
  onConfirm,
}: UploadModalProps): JSX.Element {
  // ESC closes the modal. Effect runs once whenever `open` flips on so the
  // listener only exists while a modal is mounted.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key={`upload-modal-${zone}`}
          data-testid="upload-modal"
          data-zone={zone}
          // We need the role + aria here because the underlying scene
          // (rendered behind the backdrop) still owns its own a11y semantics.
          role="dialog"
          aria-modal="true"
          aria-label="Nahrávání smlouvy"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: 'linear' }}
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6"
        >
          {/* Backdrop — translucent so the scene shows through. Click closes. */}
          <button
            type="button"
            aria-label="Zavřít"
            data-testid="upload-modal-backdrop"
            onClick={onClose}
            className="absolute inset-0 bg-pop-night/70 cursor-default"
          />

          {/* Card container — stops backdrop click propagation. */}
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.22, ease: [0, 0, 0, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md"
          >
            <button
              type="button"
              aria-label="Zavřít"
              data-testid="upload-modal-close"
              onClick={onClose}
              className="absolute -top-3 -right-3 z-10 bg-pop-torch border-[3px] border-pop-silhouette p-1 cursor-pointer hover:bg-pop-cream"
            >
              <X className="h-4 w-4 text-pop-silhouette" strokeWidth={3} />
            </button>
            <UploadFlowSteps
              zone={zone}
              onConfirm={onConfirm}
              onCancel={onClose}
              confirmLabel="Potvrdit"
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default UploadModal
